import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Typography, Steps, Alert, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { API_ORIGIN } from '../../config';

const { Title, Text } = Typography;

/**
 * Resets a client admin's password in two steps: the manager sets the new password, then confirms
 * it with the 6-digit code emailed to them. Same flow the client admin sign-in modal had.
 */
const ClientAdminResetModal = ({ open, onClose }) => {
  const [resetForm] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [resetStep, setResetStep] = useState('initiate'); // 'initiate' | 'confirm' | 'done'
  const [resetId, setResetId] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (open) {
      resetForm.resetFields();
      codeForm.resetFields();
      setResetStep('initiate');
      setResetId(null);
    }
  }, [open, resetForm, codeForm]);

  const handleInitiateReset = async (values) => {
    setResetLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_ORIGIN}/api/client-admin-auth/admin-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          clientAdminName: values.clientAdminName,
          newPassword: values.newPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to initiate reset');
      setResetId(data.resetId);
      setResetStep('confirm');
      message.success('Verification code sent to your email!');
    } catch (error) {
      message.error(error.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleConfirmReset = async (values) => {
    setResetLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_ORIGIN}/api/client-admin-auth/confirm-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          resetId,
          code: values.code,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to confirm reset');
      setResetStep('done');
      message.success('Password reset successfully!');
    } catch (error) {
      message.error(error.message);
    } finally {
      setResetLoading(false);
    }
  };

  const body = () => {
    if (resetStep === 'done') {
      return (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#12805c', marginBottom: 16 }} />
          <Title level={4} style={{ margin: '0 0 8px' }}>Password reset</Title>
          <Text type="secondary">The client admin password has been updated.</Text>
          <br /><br />
          <Button type="primary" block onClick={onClose}>Done</Button>
        </div>
      );
    }

    if (resetStep === 'confirm') {
      return (
        <>
          <Steps
            current={1}
            size="small"
            style={{ marginBottom: 24 }}
            items={[{ title: 'New password' }, { title: 'Verify code' }]}
          />
          <Alert
            message="Check your email for a 6-digit verification code. It expires in 10 minutes."
            type="info"
            showIcon
            icon={<MailOutlined />}
            style={{ marginBottom: 20 }}
          />
          <Form form={codeForm} layout="vertical" onFinish={handleConfirmReset} size="large">
            <Form.Item
              name="code"
              label="Verification code"
              rules={[
                { required: true, message: 'Please enter the verification code' },
                { len: 6, message: 'Code must be exactly 6 digits' },
                { pattern: /^\d+$/, message: 'Code must be numeric' },
              ]}
            >
              <Input
                placeholder="Enter 6-digit code"
                maxLength={6}
                style={{ letterSpacing: '0.3em', textAlign: 'center' }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={resetLoading} block>
                Confirm reset
              </Button>
            </Form.Item>
          </Form>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            style={{ marginTop: 8, padding: 0 }}
            onClick={() => { setResetStep('initiate'); setResetId(null); codeForm.resetFields(); }}
          >
            Back
          </Button>
        </>
      );
    }

    return (
      <>
        <Steps
          current={0}
          size="small"
          style={{ marginBottom: 24 }}
          items={[{ title: 'New password' }, { title: 'Verify code' }]}
        />
        <Form form={resetForm} layout="vertical" onFinish={handleInitiateReset} size="large">
          <Form.Item
            name="clientAdminName"
            label="Client admin name"
            rules={[{ required: true, message: 'Please enter the client admin name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="e.g. DanceOn" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'Please enter the new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="New password (min. 8 characters)" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={resetLoading} block>
              Send verification code
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={450} destroyOnHidden title={null}>
      <div style={{ padding: '12px 0' }}>
        <Title level={3} style={{ margin: '0 0 4px', textAlign: 'center' }}>Reset a client admin password</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
          A verification code will be sent to your email
        </Text>
        {body()}
      </div>
    </Modal>
  );
};

export default ClientAdminResetModal;
